import { match, P } from 'ts-pattern'
import { ChildPracticeMetadata, ComplianceStatus } from '../../../types'
import { PracticeCompliance, PracticeStats } from './types'

export const getPracticeCompliance = (child: ChildPracticeMetadata) => {
  const osha_compliance_status = child.osha_compliance_status === null
    ? ComplianceStatus.NotStarted
    : child.osha_compliance_status

  const hipaa_compliance_status = child.hipaa_compliance_status === null
    ? ComplianceStatus.NotStarted
    : child.hipaa_compliance_status

  const training_compliance_status = child.osha_hipaa_training_compliance_status === null
    ? ComplianceStatus.NotStarted
    : child.osha_hipaa_training_compliance_status

  const percentage_complete = child.percentage_complete === null
    ? 0
    : child.percentage_complete

  const overall_compliance_status = child.osha_hipaa_compliance_status === null
    ? ComplianceStatus.NotStarted
    : child.osha_hipaa_compliance_status

  const compliance: PracticeCompliance = {
    osha_compliance_status,
    hipaa_compliance_status,
    training_compliance_status,
    percentage_complete,
    overall_compliance_status,
  }

  return compliance
}

export const getPracticeStats = (children: PracticeCompliance[]) => {
  const stats: PracticeStats = children.reduce(
    (stats, c) => {
      match(c.hipaa_compliance_status)
        .with(P.union(ComplianceStatus.Complete), () => (stats.hipaa.completed = stats.hipaa.completed + 1))
        .with(ComplianceStatus.NotStarted, () => (stats.hipaa.notStarted = stats.hipaa.notStarted + 1))
        .with(ComplianceStatus.InProgress, () => (stats.hipaa.inProgress = stats.hipaa.inProgress + 1));

      match(c.osha_compliance_status)
        .with(P.union(ComplianceStatus.Complete), () => (stats.osha.completed = stats.osha.completed + 1))
        .with(ComplianceStatus.NotStarted, () => (stats.osha.notStarted = stats.osha.notStarted + 1))
        .with(ComplianceStatus.InProgress, () => (stats.osha.inProgress = stats.osha.inProgress + 1));

      match(c.training_compliance_status)
        .with(P.union(ComplianceStatus.Complete), () => (stats.trainingCourses.completed = stats.trainingCourses.completed + 1))
        .with(ComplianceStatus.NotStarted, () => (stats.trainingCourses.notStarted = stats.trainingCourses.notStarted + 1))
        .with(ComplianceStatus.InProgress, () => (stats.trainingCourses.inProgress = stats.trainingCourses.inProgress + 1));

      return stats
    },
    {
      osha: {
        notStarted: 0,
        completed: 0,
        inProgress: 0
      },
      hipaa: {
        notStarted: 0,
        completed: 0,
        inProgress: 0
      },
      trainingCourses: {
        notStarted: 0,
        completed: 0,
        inProgress: 0
      }
    }
  )

  return stats
}

export const getPracticeComplianceCounts = (children: PracticeCompliance[]) => {
  const complianceCounts: Record<ComplianceStatus, number> = children.reduce(
    (totalsMap, c) => ({
      ...totalsMap,
      [c.overall_compliance_status]: totalsMap[c.overall_compliance_status] + 1
    }),
    {
      [ComplianceStatus.Complete]: 0,
      [ComplianceStatus.InProgress]: 0,
      [ComplianceStatus.NotApplicable]: 0,
      [ComplianceStatus.NotStarted]: 0
    }
  )

  return complianceCounts
}

export const getPracticeCompliancePercentages = (
  children: PracticeCompliance[],
  complianceCounts: Record<ComplianceStatus, number>
) => {
  const totalPractices = children.length

  const compliancePercentages: Record<ComplianceStatus, number> = {
    [ComplianceStatus.Complete]: (complianceCounts[ComplianceStatus.Complete] / totalPractices) * 100,
    [ComplianceStatus.InProgress]: (complianceCounts[ComplianceStatus.InProgress] / totalPractices) * 100,
    [ComplianceStatus.NotApplicable]: (complianceCounts[ComplianceStatus.NotApplicable] / totalPractices) * 100,
    [ComplianceStatus.NotStarted]: (complianceCounts[ComplianceStatus.NotStarted] / totalPractices) * 100
  }

  return compliancePercentages
}
